import React, { useEffect, useMemo, useCallback } from 'react';
import {
  Outlet,
  RouteObject,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Button, DialogPlugin, Layout } from 'tdesign-react';
import { useGlobalStore } from '@/stores/context';
import { PageStatus } from '@/stores/provider';
import { ErrorBoundary } from 'react-error-boundary';
// import errorSrc from '@/assets/imgs/2.png';
import MyHeader from '@/components/Layouts/header';
import { isDesktop } from './utils';
import { UAParser } from 'ua-parser-js';

const { Content, Header, Footer } = Layout;

const noCheckPage = [
  '/home',
  '/login',
  '/extension',
  '/tips',
];
export interface RouteItemCommonProps {
  isInMenu?: boolean; // true: 显示菜单，菜单有选中状态; false: 不显示菜单; undefined: 显示菜单，菜单无选中态或选中父菜单;
  title: string;
  path: string;
  lazy?: () => Promise<{ default: () => React.ReactElement }>;
  element?: React.ReactElement;
  children?: RouteItemCommonProps[];
}

const Login = React.lazy(() => import('@/pages/Login'));
const Home = React.lazy(() => import('@/pages/Home'));
export const loadVideoAudit = () => import(/* webpackChunkName: "video-audit" */ '@/pages/VideoAudit');
const VideoAudit = React.lazy(loadVideoAudit);
const Result = React.lazy(() => import('@/pages/Result'));
const Extension = React.lazy(() => import('@/pages/Extension'));
const Error = React.lazy(() => import('@/pages/Error'));
const Tips = React.lazy(() => import('@/pages/Tips'));

// 带菜单的路由
const menuRoutes: Array<RouteItemCommonProps> = [
  {
    isInMenu: true,
    path: '/home',
    title: '视频审核',
    element: <Home />,
  },
  {
    isInMenu: true,
    path: '/',
    title: '视频审核',
    element: <Home />,
  },
  {
    isInMenu: false,
    path: '/error',
    title: '无权限',
    element: <Error />,
  },
  {
    isInMenu: false,
    path: '/login',
    title: '未登录',
    element: <Login />,
  },
  {
    isInMenu: false,
    path: '/videoAudit',
    title: '视频审核',
    element: <VideoAudit />,
  },
  {
    isInMenu: false,
    path: '/result',
    title: '审核结果',
    element: <Result />,
  },
  {
    isInMenu: false,
    path: '/extension',
    title: '延期邀请码',
    element: <Extension />,
  },
  {
    isInMenu: false,
    path: '/tips',
    title: '提示页面',
    element: <Tips />,
  },
  {
    path: '*',
    title: '404',
    element: <div style={{ padding: 32, textAlign: 'center' }}>找不到页面</div>,
  },
];

function Container(): React.ReactElement {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    state: {
      pageStatus,
      userInfo,
      userInfoList,
    },
  } = useGlobalStore();

  const checkUa = useCallback((): boolean => {
    if (!isDesktop()) {
      // const d = DialogPlugin.alert({
      //   className: 'global-dialog-button',
      //   theme: 'danger',
      //   header: '请在桌面端打开',
      //   confirmBtn: {
      //     size: 'large',
      //     children: '确认',
      //   },
      //   zIndex: 9999,
      //   onConfirm: () => d.hide(),
      //   onClose: () => d.hide()
      // });
      navigate('/tips');
      return false;
    };
    return true;
  }, []);

  const checkBrowser = useCallback((): boolean => {
    const browserList = [
      { name: 'Chrome', version: 72 },
      { name: 'Firefox', version: 66 },
      { name: 'Edge', version: 80 },
      { name: 'Sogou Explorer', version: 11, engine: 'Blink' },
      { name: 'Opera', version: 60 },
      { name: 'Safari', version: 13 },
    ];
    const parser = new UAParser();
    const result = parser.getResult();
    const { browser: { major, name }, engine } = result;
    const target = browserList.find(item => item.name === name);
    let isok = false;
    if (target && ((!target.engine && Number(major) >= target.version) || (target.engine === engine.name && Number(major) >= target.version))) {
      isok = true;
    }
    if (!isok) {
      const d = DialogPlugin.alert({
        className: 'global-dialog-button',
        theme: 'danger',
        header: '浏览器不兼容',
        body: <div>建议使用谷歌浏览器</div>,
        confirmBtn: {
          size: 'large',
          children: '确认',
        },
        zIndex: 9999,
        onConfirm: () => d.hide(),
        onClose: () => d.hide()
      });
    }
    return isok;
  }, []);

  useEffect(() => {
    if (!checkUa()) {
      return;
    };
    checkBrowser();
  }, [pathname]);

  useEffect(() => {
    // 所有工单任务完成的直接跳到结果页
    if (pageStatus === PageStatus.NORMAL && userInfoList.every(item => item.hasAuditTask)) {
      navigate('/result');
      return;
    }
    // 已登录状态就不允许跳转登录页面
    if ((pageStatus === PageStatus.NORMAL) && pathname === '/login') {
      navigate('/home');
      return;
    }
    if (pageStatus === PageStatus.NOLOGIN && !noCheckPage.includes(pathname)) {
      navigate(`/login?redirect=${location.pathname}${location.search}`);
      return;
    }
    if ((pageStatus === PageStatus.ERROR) && pathname !== '/error') {
      navigate('/error',);
    }
  }, [pageStatus, pathname, navigate, userInfoList]);


  const noShowHeader = useMemo(() => {
    return pageStatus === PageStatus.NOLOGIN || pathname === '/tips';
  }, [pageStatus, pathname]);
  const fallback = useMemo(
    () => (
      <div className='error-page-ewrapper'>
        {/* <img src={errorSrc} width={60} height={60} /> */}
        <div className='error-page-tip'>
          <div>页面异常,请稍后重试.</div>
          <div className='tip1'>
            如果问题持续存在，请 <span className='tip2' >反馈</span> 给我们，我们会尽快帮您解决。
          </div>
        </div>
        <Button className='goto-main' onClick={() => location.replace(`/`)} >首页</Button>
      </div>
    ),
    [],
  );
  const onError = useCallback((error: Error) => {
    console.log('error', error);
  }, []);
  // if (pageStatus === PageStatus.NOLOGIN && pathname !== '/login') return <Loading loading={true} />;

  return (
    <Layout
      style={{ height: '100%' }}
    >
      {
        !noShowHeader && (
          <Header className={`v-header}`} >
            <MyHeader />
          </Header>)
      }
      <Content
        className='v-content'
      >
        <div className='v-content-main'>
          <ErrorBoundary fallback={fallback} onError={onError}>
            <div className="v-body">
              <Outlet></Outlet>
            </div>
          </ErrorBoundary>
        </div>
      </Content>
    </Layout>
  );
}

const Routes: Array<RouteObject> = [
  // 如果有独立页面路由可以加在这里
  {
    path: '/',
    element: <Container />,
    children: menuRoutes as unknown as RouteObject[],
  },
];
// const Routes: Array<RouteObject> = menuRoutes as unknown as RouteObject[];

export default Routes;